import config from '../config';

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
  lockoutOnFailure?: boolean;
}

export interface LoginResponse {
  jwtToken: string;
  succeeded: boolean;
  requiresTwoFactor: boolean;
  isLockedOut: boolean;
}

export interface RegisterRequest {
  email: string;
  password: string;
}

export interface RegisterError {
  code: string;
  description: string;
}

export interface RegisterResponse {
  success: boolean;
  userId: string;
  errors: RegisterError[];
}

function buildUrl(path: string): string {
  // Ensure no double slashes when concatenating
  // Identity endpoints are now served from the main API
  const left = config.apiBaseUrl.replace(/\/$/, '');
  const right = path.startsWith('/') ? path : `/${path}`;
  return `${left}${right}`;
}

async function postJson<TRequest, TResponse>(url: string, body: TRequest): Promise<TResponse> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  const parsed = await response.json();
  if (!response.ok) {
    // Allow callers to inspect JSON error payloads from the API
    const error = new Error(`Request failed with status ${response.status}`) as Error & { details?: unknown };
    error.details = parsed;
    throw error;
  }
  return parsed as TResponse;
}

export async function login(request: LoginRequest): Promise<LoginResponse> {
  const url = buildUrl(config.identityPaths.login);
  return await postJson<LoginRequest, LoginResponse>(url, request);
}

export async function register(request: RegisterRequest): Promise<RegisterResponse> {
  const url = buildUrl(config.identityPaths.register);
  return await postJson<RegisterRequest, RegisterResponse>(url, request);
}

export interface ConfirmEmailRequest {
  userId: string;
  code: string;
}

export interface ConfirmEmailResponse {
  success: boolean;
  message: string;
}

export async function confirmEmail(request: ConfirmEmailRequest): Promise<ConfirmEmailResponse> {
  const url = buildUrl(config.identityPaths.confirmEmail);
  return await postJson<ConfirmEmailRequest, ConfirmEmailResponse>(url, request);
}

export interface ResendConfirmationRequest {
  email: string;
}

export interface ResendConfirmationResponse {
  success: boolean;
  message: string;
}

export async function resendConfirmation(request: ResendConfirmationRequest): Promise<ResendConfirmationResponse> {
  const url = buildUrl(config.identityPaths.resendConfirmation);
  return await postJson<ResendConfirmationRequest, ResendConfirmationResponse>(url, request);
}


